import { type ReactNode } from "react";
import classNames from "classnames";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { useConfiguratorType } from "~/features/configurator/common/useConfiguratorType";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { Alert } from "~/common/mui/Alert/Alert";
import { styled } from "~/common/mui";
import CheckIcon from "@mui/icons-material/Check";

export const FrameRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingTop: theme.spacing(3.75),
  position: "relative",
  "&&.picked": {
    "& > :first-child": {
      color: theme.palette.primary.main,
    },
  },
}));

export const FrameCount = styled("div")({
  display: "flex",
  alignItems: "center",
});

export const FrameTitle = styled("h3")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginTop: theme.spacing(0.625),
  marginBottom: 0,
}));

export const FrameTile = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "auto",
  justifyContent: "flex-start",
  width: "100%",
  borderBottom: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
}));

interface Props {
  className?: string;
  title: string;
  csticIndex: number;
  csticAmount: number;
  children: ReactNode;
  info?: string;
  description?: string;
  isPicked: boolean;
  showError?: boolean;
  errorMessage: string;
}

const SubstepFrame = (props: Props) => {
  const { className, title, description, csticAmount, csticIndex, isPicked, showError, errorMessage, info } = props;

  const configuratorType = useConfiguratorType();
  const isOldConfigurator = configuratorType === ConfiguratorType.System;

  const externalContentCls = isOldConfigurator
    ? "scha__lc__external-content--shaft"
    : "scha__lc__external-content--shaft";

  return (
    <FrameRoot className={classNames(className, isPicked && "picked")}>
      <FrameCount>
        <CheckIcon className={"image-scale-up"} sx={{ fontSize: 20 }} />
        <span className={"ml-2"}>
          {csticIndex}/{csticAmount}
        </span>
      </FrameCount>
      <FrameTitle>
        <LanguageAwareExternalContent content={title} className={"scha__lc__external-label"} />
        <LinearInfo info={info} popupClassName={externalContentCls} />
      </FrameTitle>
      {description && (
        <div className="mt-3">
          <LanguageAwareExternalContent content={description} className={"scha__lc__external-label"} />
        </div>
      )}
      {showError && (
        <Alert className={"mt-30"} severity="error" data-message="error">
          {errorMessage}
        </Alert>
      )}
      <FrameTile className="mt-30">{props.children}</FrameTile>
    </FrameRoot>
  );
};

export default SubstepFrame;
